import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {setWindowTitle} from 'BenchlingUtil';
import BufferOptions from 'buffers/BufferOptions';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {clearNavigationWarnings} from 'lib/navigateToURL';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {getMaterialFlows} from 'procedures/design/flowchartManagementUtils';
import LegacyProcedureEditor from 'procedures/design/LegacyProcedureEditor';
import {MethodEditorTab} from 'procedures/design/method-config/MethodConfigConstants';
import useValidateProcedure from 'procedures/design/method-config/useValidateProcedure';
import ProcedureEditorHeader from 'procedures/design/ProcedureEditorHeader';
import {HistoryManagerContext} from 'procedures/design/ProcedureHistoryPanel';
import useEditingMethodNameState from 'procedures/design/useEditingMethodNameState';
import useLegacyPreSaveImmediatelyMiddleware from 'procedures/design/useLegacyPreSaveImmediatelyMiddleware';
import determineProcedureAccessLevel, {
  ProcedureAccessLevel,
} from 'procedures/design/validation/determineProcedureAccessLevel';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedurePageThinQuery_procedure_ProcedureNode as ThinNetworkProcedure} from 'procedures/graphql/__generated/ProcedurePageThinQuery';
import parseProcedureFromNetwork from 'procedures/network/parseProcedureFromNetwork';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import serializeProcedureFlowchartForNetwork from 'procedures/network/serializeProcedureFlowchartForNetwork';
import {
  MaterialFlow,
  ProcedureFlowchart,
  ProcedureMetadata,
  ProcedureWithMetadata,
} from 'procedures/ProcedureDomainTypes';
import {normalizeFlowchartOrderingForComparison} from 'procedures/procedurePagePersistenceUtils';
import {ProcedureProcessesPlugin} from 'procedures/procedures-platform/ProcedureProcessesPlugin';
import duplicateProcedure from 'procedures/utils/duplicateProcedure';
import flushDocumentStore from 'procedures/utils/flushDocumentStore';
import {useAllTagSchemasWithStandardShape} from 'procedures/utils/useAllTagSchemas';
import ProcessFlowchartConfigEditor from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditor';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';
import getTestProps from 'test/lib/cypress/getTestProps';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import memoizeLastCall from 'util/memoizeLastCall';

import useLegacyProcedureSaveTools from './design/useLegacyProcedureSaveTools';
import useProcedureActions from './design/useProcedureActions';
import useProcedureReadonlyState from './design/useProcedureReadonlyState';
import {IdToCidMap, invertStringMapping} from './network/procedureNetworkUtils';
import {
  GetCachedCidFunctionFactory,
  INACCESSIBLE_FLOWCHART_VERSION_ID,
  NetworkProcedure,
  LegacyProcedureAutosaveManager,
  ProcedureCapabilityChecker,
  ProcedureFlowchartStateManager,
  ProcedureLegacyFlowchartVersionLoader,
  ProcedureHardRefreshManager,
  ProcedureLoader,
  ProcedureVersionHistoryManager,
  useProcedurePageIdToCidMap,
  ProcedureVersionInfoMerger,
} from './ProcedurePageComponents';
import {PersistenceInfo, ProcedurePageContext, ProcedurePageContextValue} from './ProcedurePageContext';

interface ProcedurePageProps {
  procedureId: string;
  flowchartVersionId?: string;
}

function getLatestVersionId(
  thinProcedureData: ThinNetworkProcedure,
  getLegacyVersionIdEvenIfInaccessible?: boolean
): string {
  if (
    thinProcedureData.processFlowchartConfig != null &&
    BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION
  ) {
    const fccv = thinProcedureData.processFlowchartConfig.currentFlowchartConfigVersion;
    return fccv && !isInaccessible(fccv) ? fccv.id : INACCESSIBLE_FLOWCHART_VERSION_ID;
  }

  const {latestFlowchartVersion} = thinProcedureData.procedureFlowchart;
  return !isInaccessible(latestFlowchartVersion)
    ? latestFlowchartVersion.id
    : getLegacyVersionIdEvenIfInaccessible
      ? latestFlowchartVersion.invalidId
      : INACCESSIBLE_FLOWCHART_VERSION_ID;
}

export default function ProcedurePage({
  procedureId,
  flowchartVersionId,
}: ProcedurePageProps): JSX.Element | null {
  /**
   * The {key} props in the components below are specific and intentional, to ensure that
   * we keep existing component instances whenever we can.
   */
  return (
    <ProcedureHardRefreshManager>
      {(hardRefresh, refreshKey) => (
        // First: load the procedure (without the flowchart)
        <ProcedureLoader procedureId={procedureId} key={refreshKey}>
          {(thinProcedureData, refetchProcedure) => (
            // Next: the version history mgr tracks old versions, which one is visible, manages the URL
            <LegacyProcedureAutosaveManager procedure={thinProcedureData}>
              <ProcedureVersionHistoryManager
                procedureId={procedureId}
                flowchartConfigId={thinProcedureData.processFlowchartConfig?.id}
                latestVersionId={getLatestVersionId(thinProcedureData)}
                flowchartVersionId={flowchartVersionId}
                // TODO(BNCH-137457): We need to support PoW publishing, which will require updating this to use the PoW published flowchart version
                // and PoW flag is enabled.
                publishedFlowchartVersionId={
                  thinProcedureData.procedureFlowchart.publishedFlowchartVersion?.id
                }
                publishingRecords={accessibleObjs(thinProcedureData.publishingRecords)}
              >
                {(versionIdToView, setVersionIdToView) => (
                  // Next: ensure we have sufficient capabilities to view the procedure
                  <ProcedureCapabilityChecker procedureData={thinProcedureData}>
                    {/* Next: load the requested flowchart version (if not provided, or if it's PoW: the latest version) */}
                    <ProcedureLegacyFlowchartVersionLoader
                      procedureData={thinProcedureData}
                      procedureFlowchartVersionId={versionIdToView}
                      key={versionIdToView}
                    >
                      {(
                        fullProcedureData,
                        refetchLegacyFlowchartVersion,
                        loadedFlowchartVersion,
                        isViewingOldVersion
                      ) => (
                        <ProcedureVersionInfoMerger
                          fullProcedureData={fullProcedureData}
                          loadedLegacyFlowchartVersion={loadedFlowchartVersion}
                          isViewingOldLegacyVersion={isViewingOldVersion}
                        >
                          {(versionMetadata) => (
                            // Finally, load the write manager, which also renders the procedure page
                            <ProcedurePageWriteManager
                              procedureGraphQL={fullProcedureData}
                              versionMetadata={versionMetadata}
                              refetchLegacyProcedure={() => refetchLegacyFlowchartVersion(refetchProcedure)}
                              hardRefresh={hardRefresh}
                              onSwitchVersionId={setVersionIdToView}
                            />
                          )}
                        </ProcedureVersionInfoMerger>
                      )}
                    </ProcedureLegacyFlowchartVersionLoader>
                  </ProcedureCapabilityChecker>
                )}
              </ProcedureVersionHistoryManager>
            </LegacyProcedureAutosaveManager>
          )}
        </ProcedureLoader>
      )}
    </ProcedureHardRefreshManager>
  );
}

/**
 * This function performs a "clean" save: it doesn't push any values into state or redux. It just calls the API.
 */
async function saveLegacyProcedure(
  procedure: ProcedureWithMetadata,
  persistenceInfo?: PersistenceInfo,
  lastSavedFlowchartVersionId?: string
): Promise<Record<string, string>> {
  const {
    metadata: {name, id, templateCollectionId},
  } = procedure;

  let updatedId: string = '';
  let cidToIdMap: Record<string, string> = {};

  const result = await ProcedureDAO.updateProcedure({
    id,
    name,
    flowchart: serializeProcedureFlowchartForNetwork(
      procedure.flowchart,
      procedure.metadata.procedureType,
      persistenceInfo,
      lastSavedFlowchartVersionId
    ),
    templateCollectionId,
  });

  updatedId = result.updatedIds[0];
  cidToIdMap = result.cidToIdMap;

  assert(id === updatedId, 'updated procedure id does not match provided id');
  return cidToIdMap;
}

/**
 * When we unmount, we need to curry in the lastSavedFlowchartVersionId, to ensure it's provided correctly
 */
function saveLegacyProcedureWhenUnmountedFactory(
  lastSavedFlowchartVersionId: string
): (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => Promise<null> {
  let saved = false;

  return async (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => {
    if (saved) {
      // Ensure that we don't save the procedure multiple times after it's been unmounted
      return null;
    }
    await saveLegacyProcedure(procedure, persistenceInfo, lastSavedFlowchartVersionId);
    saved = true;

    // Clean up the document store, to ensure we don't have any stale IDs. Without this cleanup,
    // it's fairly easy to end up with a server error (mismatched version) the next time we autosave.
    flushDocumentStore(procedure);
    return null;
  };
}

type UpdateLegacyProcedure = (
  procedure: ProcedureWithMetadata,
  persistenceInfo?: PersistenceInfo
) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;

const memoizedParseProcedureFromNetwork = memoizeLastCall(
  (procedureGraphQL: NetworkProcedure, getCachedCidFactory: GetCachedCidFunctionFactory) =>
    parseProcedureFromNetwork(procedureGraphQL, getCachedCidFactory()),
  {useDeepEquality: true}
);

const memoizedGetCachedCidFactory = memoizeLastCall(
  (map: IdToCidMap, getCachedCidFactory: GetCachedCidFunctionFactory) => () => getCachedCidFactory(map),
  {
    useDeepEquality: true,
  }
);

/** @package Exported for testing
 *
 * TODO(BNCH-137457): This component needs to be refactored to support PoW publishing. For now, it won't be updated
 * to support both legacy and PoW publishing. This will be done once we are ready to support PoW publishing.
 * Keep in mind that when doing the refactoring, we need to keep this backwards compatible and only select the
 * PoW latest flowchart version when the PoW flag is enabled and the procedure flowchart config version proxy is available.
 */
export function ProcedurePageWriteManager(props: {
  procedureGraphQL: NetworkProcedure;
  versionMetadata: {id: string; createdAt: string; isLatest: boolean};
  onSwitchVersionId: (versionId: string) => void;
  refetchLegacyProcedure: () => Promise<NetworkProcedure | null> | null;
  hardRefresh: () => void;
}): JSX.Element {
  const isMountedRef = useIsNotUnmounted();

  // The last known flowchart version (or flowchart config version) per-client that we will be saving updates on top of.
  // This will be used by the server to determine if the session is using a stale version of the procedure
  // to prevent concurrent edits from overwriting each other
  const lastSavedFlowchartVersionId = useRef<string>(getLatestVersionId(props.procedureGraphQL, true));
  const [fullyBlockedOnServerError, setFullyBlockedOnServerError] = useStateIfMounted(false);
  const {idToCidMap, setIdToCidMap, getCachedCidFactory} = useProcedurePageIdToCidMap(props.procedureGraphQL);
  const currentProcedure = useMemo(
    () => parseProcedureFromNetwork(props.procedureGraphQL, getCachedCidFactory()),
    [props.procedureGraphQL, getCachedCidFactory]
  );
  const {refetchLegacyProcedure} = props;

  const updateLegacyProcedure: UpdateLegacyProcedure = useCallback(
    async (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => {
      let cidToIdMap;
      try {
        cidToIdMap = await saveLegacyProcedure(
          procedure,
          persistenceInfo,
          lastSavedFlowchartVersionId.current
        );
      } catch (e) {
        if (e.status === 409) {
          // Any error that should force the procedure into a read-only state that is recovered on refresh
          // Should be handled here. Currently this handles 409 state conflicts.
          setFullyBlockedOnServerError(true);
        }
        throw e;
      }

      const newIdToCidMap = invertStringMapping(cidToIdMap);

      // The next render cycle triggered by this setState call will NOT see the most up-to-date refetched ids
      setIdToCidMap((cachedIdToCidMap) => ({...cachedIdToCidMap, ...newIdToCidMap}));
      // The next render cycle triggered by this refetch call will see the most up-to-date refetched ids
      const updatedNetworkProcedure = await refetchLegacyProcedure();
      // The next render cycle triggered by this setState call will see the most up-to-date refetched ids
      setIdToCidMap(newIdToCidMap);

      if (isMountedRef.current) {
        // The call to `refetch` may return null. This happens if the component has been unmounted in the interim.
        // In that case, the return value here isn't important.
        let updatedProcedure = procedure;
        if (updatedNetworkProcedure != null) {
          assert(
            !isInaccessible(updatedNetworkProcedure.procedureFlowchart.latestFlowchartVersion),
            'Expected latest flowchart version to be accessible after write'
          );
          lastSavedFlowchartVersionId.current =
            updatedNetworkProcedure.procedureFlowchart.latestFlowchartVersion.id;
          updatedProcedure = memoizedParseProcedureFromNetwork(
            updatedNetworkProcedure,
            memoizedGetCachedCidFactory(newIdToCidMap, getCachedCidFactory)
          );
        }

        return {
          updatedProcedure,
          cidToIdMap,
        };
      } else {
        return null;
      }
    },
    [setIdToCidMap, getCachedCidFactory, refetchLegacyProcedure, isMountedRef, setFullyBlockedOnServerError]
  );

  return (
    <ProcedureFlowchartStateManager
      procedureId={props.procedureGraphQL.id}
      persistedFlowchart={currentProcedure.flowchart}
      procedureMetadata={currentProcedure.metadata}
    >
      {(
        persistedFlowchart,
        draftFlowchart,
        draftFlowchartRef,
        setDraftFlowchart,
        procedureMetadata,
        setProcedureMetadata,
        {dirtyState, readonlyState, isSaveInFlightState, isAutosaverPausedState, isAutosaverBlockedState}
      ) => (
        <PresentationalProcedurePage
          procedureMetadata={procedureMetadata}
          processFlowchartConfigId={currentProcedure.processFlowchartConfigId}
          persistedFlowchart={persistedFlowchart}
          draftFlowchart={draftFlowchart}
          draftFlowchartRef={draftFlowchartRef}
          setDraftFlowchart={setDraftFlowchart}
          setProcedureMetadata={setProcedureMetadata}
          dirtyState={dirtyState}
          readonlyState={readonlyState}
          isSaveInFlightState={isSaveInFlightState}
          isAutosaverPausedState={isAutosaverPausedState}
          isAutosaverBlockedState={isAutosaverBlockedState}
          duplicateProcedure={(params) => duplicateProcedure({...params, currentProcedure})}
          updateAndPersistLegacyProcedure={updateLegacyProcedure}
          idToCidMap={idToCidMap}
          fullyBlockedOnServerError={fullyBlockedOnServerError}
          onSwitchVersionId={props.onSwitchVersionId}
          publishingRecords={accessibleObjs(props.procedureGraphQL.publishingRecords)}
          currentFlowchartVersionMetadata={props.versionMetadata}
          lastSavedFlowchartVersionId={lastSavedFlowchartVersionId}
          hardRefresh={props.hardRefresh}
          key={currentProcedure.metadata.id}
        />
      )}
    </ProcedureFlowchartStateManager>
  );
}

interface PresentationalProcedurePageProps {
  procedureMetadata: ProcedureMetadata;
  processFlowchartConfigId: string | null;
  persistedFlowchart: ProcedureFlowchart;
  draftFlowchart: ProcedureFlowchart;
  draftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>;
  setDraftFlowchart: (fc: ProcedureFlowchart) => void;
  setProcedureMetadata: (procedureMetadata: ProcedureMetadata) => void;
  dirtyState: [boolean, (isDirty: boolean) => void];
  readonlyState: [boolean, (isReadonly: boolean) => void];
  isSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
  isAutosaverPausedState: [boolean, (isPaused: boolean) => void];
  isAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
  updateAndPersistLegacyProcedure: (
    procedure: ProcedureWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;
  duplicateProcedure: (params: {
    name: string;
    templateCollectionId: string;
    procedureType: ProcedureType;
  }) => Promise<void>;
  idToCidMap: Record<string, string>;
  // If the WriteManager determines we should block the user from making changes due to an error.
  // This will make the procedure read-only in its unsaved state and force the user to refresh
  fullyBlockedOnServerError: boolean;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  lastSavedFlowchartVersionId: React.MutableRefObject<string>;
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
  hardRefresh: () => void;
  onSwitchVersionId: (versionId: string) => void;
}

/**
 * All about draft state management:
 *
 * We keep three (!) copies of the flowchart in memory. This explains why, and what they are each used for.
 *
 * [1] REDUX
 *
 * This is the source of truth for the flowchart. The other two are just for convenience and/or temporal correctness,
 * as described below. In this component, the Redux store is accessed via the props `draftFlowchart` and `setDraftFlowchart`.
 * These props are provided by ProcedureFlowchartStateManager, which mediates between the UI and Redux.
 *
 * [2] REF
 *
 * The `draftFlowchartRef` prop is also managed by ProcedureFlowchartStateManager. Any changes to the flowchart are
 * immediately saved in the ref, at the same time that they are sent to Redux.
 *
 * The value of using the ref is that it is immune to closure staleness. Even if the ref is stale, the contents of
 * the flowchart are stored one level deeper (ref.current), so changes made to it are available immediately and reliably.
 * This has been necessary to avoid stale flowchart data in `handleSave`, which does a lot of async juggling.
 *
 * [3] LOCAL COMPONENT STATE
 *
 * Finally, the component below has its own local state variable for the draft flowchart. This is needed because
 * we've built out a lot of `setState` calls using the modifier callback form: `setDraftFlowchart(fc => fc)`.
 * Unfortunately, we can't do this with Redux, which requires a hard setter: `setDraftFlowchart(fc)`.
 * In order to keep the modifier callbacks working, we use local state here, and push changes up to Redux
 * in response to those changes via an effect hook.
 *
 * The local state value should not be inspected or used. It is for temporary storage only, until the value
 * can be pushed to Redux.
 *
 * [SUMMARY]
 *
 *    draftFlowchart (redux)         -> √ The source of truth
 *    setDraftFlowchart (redux)      -> X Should not be called directly, other than in response to changes to localDraftFlowchart
 *    localDraftFlowchart (local)    -> X Should not be inspected or used, other than to send to Redux
 *    setLocalDraftFlowchart (local) -> √ Use this on changes to the flowchart
 *    draftFlowchartRef              -> √ Use during saves, or other async operations that require perfect data
 *
 */
export function PresentationalProcedurePage({
  procedureMetadata,
  setProcedureMetadata,
  processFlowchartConfigId,
  persistedFlowchart,
  draftFlowchart,
  draftFlowchartRef,
  setDraftFlowchart: _setDraftFlowchart,
  dirtyState,
  readonlyState,
  isSaveInFlightState,
  isAutosaverPausedState,
  isAutosaverBlockedState,
  updateAndPersistLegacyProcedure,
  duplicateProcedure,
  idToCidMap,
  fullyBlockedOnServerError,
  currentFlowchartVersionMetadata,
  lastSavedFlowchartVersionId,
  publishingRecords,
  hardRefresh,
  onSwitchVersionId,
}: PresentationalProcedurePageProps): JSX.Element {
  const isProcedurePoW =
    processFlowchartConfigId != null && BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION;
  const {id: procedureId} = procedureMetadata;
  const [_localDraftFlowchartPrivate, _setLocalDraftFlowchartPrivate] =
    useStateIfMounted<ProcedureFlowchart>(persistedFlowchart);
  const [materialFlows, setMaterialFlows] = useStateIfMounted<Array<MaterialFlow>>(
    getMaterialFlows(persistedFlowchart)
  );
  const [isProcedureDirty, setProcedureDirty] = dirtyState;
  const [isEditingMethodTab, setIsEditingMethodTab] = useState<boolean>(false);
  const [isSaving] = isSaveInFlightState;
  const [selectedMethodCid, setSelectedMethodCid] = useState<string | null>(null);
  const [accessLevel, setAccessLevel] = useStateIfMounted<null | ProcedureAccessLevel>(null);
  const [anyTabValidationErrors, setAnyTabValidationErrors] = useState(false);
  // Increment this value to force the ProcedureEditor component to be rebuilt by flushing its internal state
  // when staged and unsaved changes are reset via resetDraftFlowchartChanges
  const [procedureEditorKey, setProcedureEditorKey] = useStateIfMounted(0);
  // Downstream components can patch into calls to `saveImmediately`
  const {addLegacyMiddleware, removeLegacyMiddleware, legacyMiddleware} =
    useLegacyPreSaveImmediatelyMiddleware();
  const editingMethodNameState = useEditingMethodNameState(draftFlowchart, procedureMetadata.procedureType);
  // The versionID is only needed in PoW. In legacy procedures, this component already gets the fully loaded procedure object.
  // But in PoW, the version ID from the history manager is handed to the PoW renderer, which loads the version as needed.
  const {versionIdToView} = HistoryManagerContext.useContext();

  // Changes to the local draft flowchart are reported to the state manager, who pushes it into redux
  useEffect(() => {
    _setDraftFlowchart(_localDraftFlowchartPrivate);
  }, [_localDraftFlowchartPrivate, _setDraftFlowchart]);

  const setLocalDraftFlowchart = useCallback(
    // Use state updater callback so long running async threads can set draft flowchart state based on the
    // current state instead of stale state from when the async thread started.
    (fcUpdater: (fc: ProcedureFlowchart) => ProcedureFlowchart): void => {
      _setLocalDraftFlowchartPrivate((pendingFc) => {
        const updatedFc = fcUpdater(pendingFc);
        if (
          !isEqual(
            normalizeFlowchartOrderingForComparison(updatedFc),
            normalizeFlowchartOrderingForComparison(persistedFlowchart)
          )
        ) {
          setProcedureDirty(true);
        }
        setMaterialFlows(getMaterialFlows(updatedFc));
        return updatedFc;
      });
    },
    [_setLocalDraftFlowchartPrivate, persistedFlowchart, setMaterialFlows, setProcedureDirty]
  );

  // Create stable refs for saveImmediately to avoid unnecessary re-creation. Without these, the
  // `saveImmediately` function would change whenever we update the callback function `setLocalDraftFlowchart`
  // which does not use `useState`, so it will change whenever the `persistedFlowchart` changes.
  // If we don't do the jump through the `ref`, then we end up recreating the `saveImmediately` function which
  // then causes unnecessarily effects downstream.
  const setLocalDraftFlowchartRef = useRef(setLocalDraftFlowchart);
  const legacyMiddlewareRef = useRef(legacyMiddleware);

  useEffect(() => {
    setLocalDraftFlowchartRef.current = setLocalDraftFlowchart;
    legacyMiddlewareRef.current = legacyMiddleware;
  }, [setLocalDraftFlowchart, legacyMiddleware]);

  const resetDraftFlowchartChanges = useCallback((): void => {
    setProcedureDirty(false);
    // Trigger re-render of ProcedureEditor and flush its internal state
    setProcedureEditorKey((c) => c + 1);
    setLocalDraftFlowchart(() => persistedFlowchart);
  }, [setProcedureDirty, setProcedureEditorKey, setLocalDraftFlowchart, persistedFlowchart]);

  // Utility to defer running setDraftFlowchart till pending updates to persistedFlowchart are visible.
  // This is only needed when the code causes a change in persistedFlowchart (eg. refetch) and tries to call
  // setDraftFlowchart within the same synchronous code path. That code would see a stale value for
  // persistedFlowchart.
  const [pendingDraftFlowchartUpdate, setPendingDraftFlowchartUpdate] = useStateIfMounted<{
    updater: (fc: ProcedureFlowchart) => ProcedureFlowchart;
  } | null>(null);

  useEffect(() => {
    if (pendingDraftFlowchartUpdate != null) {
      setLocalDraftFlowchart(pendingDraftFlowchartUpdate.updater);
      setPendingDraftFlowchartUpdate(null);
    }
  }, [pendingDraftFlowchartUpdate, setLocalDraftFlowchart, setPendingDraftFlowchartUpdate]);

  useAllTagSchemasWithStandardShape((schemas) => {
    setAccessLevel(determineProcedureAccessLevel(draftFlowchart, schemas));
  });

  useEffect(() => {
    setWindowTitle(procedureMetadata.name);
  }, [procedureMetadata.name]);

  const {readOnlyBannerMessage, isProcedureReadOnly, isProcedureArchived} = useProcedureReadonlyState({
    draftFlowchart,
    procedureMetadata,
    procedureId,
    accessLevel,
    fullyBlockedOnServerError,
    currentFlowchartVersionMetadata,
    publishingRecords,
    readonlyState,
    isAutosaverBlockedState,
    isSaveInFlightState,
  });

  const procedureValidationErrors = useValidateProcedure(
    draftFlowchart,
    procedureMetadata.procedureType,
    procedureId
  );

  /**
   * "Blocked" is a fundamental situation. E.g., server error. This should not be controlled downstream.
   * "Paused" is a UX concern. E.g., during a modal editing operation that requires quiet.
   */
  const [shouldPauseAutosave, setShouldPauseAutosave] = isAutosaverPausedState;

  const legacySaveImmediately = useLegacyProcedureSaveTools({
    isProcedurePoW,
    procedureId,
    processFlowchartConfigId,
    draftFlowchartRef,
    legacyMiddlewareRef,
    setLocalDraftFlowchartRef,
    setProcedureMetadata,
    updateAndPersistLegacyProcedure,
    setPendingDraftFlowchartUpdate,
    lastSavedFlowchartVersionId,
    saveProcedureWhenUnmountedFactory: saveLegacyProcedureWhenUnmountedFactory,
  });

  const {
    handleRename,
    handleMove,
    handleArchive,
    handleUnarchive,

    // These tools were used in the legacy procedures header
    handleLegacyModifyFlowchart,
    handleLegacyStopEditingMethod,
    handleLegacyStartEditingMethod,
    handleLegacyAddMethod,
  } = useProcedureActions({
    isProcedurePoW,
    procedureMetadata,
    setProcedureMetadata,
    legacySaveImmediately,
    procedureId,
    hardRefresh,
    setLocalDraftFlowchart,
    draftFlowchartRef,
    selectedMethodCid,
    setSelectedMethodCid,
    autofocusEditNameOnNewMethod: editingMethodNameState.autofocusEditNameOnNewMethod,
  });

  useEffect(() => {
    // Displays confirmation on close tab and reload event
    const handleDisplayCloseTabAndReload = (e: BeforeUnloadEvent): void => {
      if (isProcedureDirty) {
        e.preventDefault();
        e.returnValue = ''; // needed to make default pop up show when closing tab
      }
    };

    window.addEventListener('beforeunload', handleDisplayCloseTabAndReload);
    return () => {
      clearNavigationWarnings();
      window.removeEventListener('beforeunload', handleDisplayCloseTabAndReload);
    };
  }, [isProcedureDirty]);

  const [currentTab, _setCurrentTab] = useState(MethodEditorTab.DEFINITIONS);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const procedurePageContextValue = {
    isProcedureDirty,
    isSaving,
    legacySaveImmediately,
    registerLegacyPreSaveImmediatelyMiddleware: addLegacyMiddleware,
    unregisterLegacyPreSaveImmediatelyMiddleware: removeLegacyMiddleware,
    selectedMethodCid,
    setSelectedMethodCid,
    isEditingMethodTab,
    setIsEditingMethodTab,
    shouldPauseAutosave,
    setShouldPauseAutosave,
    idToCidMap,
    materialFlows,
    resetDraftFlowchartChanges,
    accessLevel,
    procedureId,
    procedureType: procedureMetadata.procedureType,
    currentTab,
    dangerouslySetCurrentTab: _setCurrentTab,
    procedureTemplateCollectionOwner: procedureMetadata.templateCollectionOwner,
  };
  const validationContextValue = useMemo(
    () => ({
      procedureValidationErrors,
      anyTabValidationErrors,
      setAnyTabValidationErrors,
    }),
    [procedureValidationErrors, anyTabValidationErrors, setAnyTabValidationErrors]
  );

  const structuredTableInsertionContextValue = useMemo(
    () => ({itemInsertedFromId: procedureId}),
    [procedureId]
  );

  return (
    <div
      {...getTestProps({component: 'ProcedurePage', element: 'root'})}
      style={{display: 'flex', flexDirection: 'column', height: '100%'}}
    >
      <ProcedurePageContext.Provider value={procedurePageContextValue}>
        <ProcedureValidationContext.Provider value={validationContextValue}>
          <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
            <ProcedureEditorHeader
              procedureMetadata={procedureMetadata}
              methodNodes={draftFlowchart.nodes}
              onDuplicate={duplicateProcedure}
              onRename={handleRename}
              onMove={handleMove}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onAddMethod={() => {
                handleLegacyAddMethod({shouldSwitchViewToFlowchart: true, shouldOpenNameEditor: true});
              }}
              currentFlowchartVersionMetadata={currentFlowchartVersionMetadata}
              hardRefresh={hardRefresh}
              onSwitchVersionId={onSwitchVersionId}
              isProcedureReadOnly={isProcedureReadOnly}
              key={procedureId}
            />
            {readOnlyBannerMessage && <BufferOptions banner={readOnlyBannerMessage} fullScreen={false} />}
            {BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION && processFlowchartConfigId != null ? (
              <ProcessFlowchartConfigEditor
                flowchartTypeName={ProcedureProcessesPlugin.FLOWCHART_TYPE_NAME}
                flowchartConfigId={processFlowchartConfigId}
                flowchartConfigVersionId={versionIdToView}
                locked={isProcedureReadOnly}
              />
            ) : (
              <LegacyProcedureEditor
                flowchart={draftFlowchartRef.current}
                modifyFlowchart={handleLegacyModifyFlowchart}
                procedureId={procedureMetadata.id}
                templateCollectionId={procedureMetadata.templateCollectionId}
                isProcedureReadOnly={isProcedureReadOnly}
                isProcedureArchived={isProcedureArchived}
                procedureEditorKey={procedureEditorKey}
                onStopEditingMethod={handleLegacyStopEditingMethod}
                onStartEditingMethod={handleLegacyStartEditingMethod}
                editingMethodNameState={editingMethodNameState}
              />
            )}
            <ProcedureContextRetriever pageContextValue={procedurePageContextValue} />
          </StructuredTableInsertionContext.Provider>
        </ProcedureValidationContext.Provider>
      </ProcedurePageContext.Provider>
    </div>
  );
}

// Used for testing only
function ProcedureContextRetriever(props: {pageContextValue: ProcedurePageContextValue}): null {
  return null;
}

const proceduresPlugin = new ProcedureProcessesPlugin();
proceduresPlugin.registerForEditor();
